import type { MutableRefObject } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import CustomerLogo from "@/components/customers/CustomerLogo";
import CustomerStatusPill from "@/components/customers/CustomerStatusPill";
import type { CustomerActivityType, DBSignalQueueItem, OpportunityStage } from "@/lib/db-types";
import { activityTypeLabel } from "@/lib/customer-activity-calc";
import { contactLabel } from "@/lib/contact-display";
import { formatDate, formatNOK, formatRelativeTime } from "@/lib/format";
import { STAGE_LABELS } from "@/lib/opportunity-calc";
import { signalSourceLabel, signalTypeLabel } from "@/lib/signal-types";
import { cn } from "@/lib/utils";
import Kbd from "./QueueKbd";
import SwipeCard from "./SwipeCard";

const NEW_PROCESS = "new";

interface SignalCardProps {
  signal: DBSignalQueueItem;
  /** Plass i køen (1-basert) og antall signaler totalt. */
  position: number;
  total: number;
  isAdmin: boolean;
  pending: boolean;
  /** Prosessen signalet legges i; null = ny prosess. */
  processId: string | null;
  onProcessChange: (id: string | null) => void;
  onEmail: () => void;
  onPhone: () => void;
  onLater: () => void;
  /** Kalles når sveipet er ferdig. */
  onDismiss: () => void;
  onArchiveAll: () => void;
  /** Lar køen starte sveipet fra pil venstre. */
  dismissRef?: MutableRefObject<(() => void) | null>;
}

/**
 * Ett signalkort i køen (VIB-2958): kunden med logo, signalet i en grå boks,
 * hvorfor Sølvrev bør ta kontakt, tre fakta og fire handlinger. E-post, Ring og
 * Følg opp senere tar signalet; Avvis sveiper kortet ut til venstre («ikke for meg»).
 */
export default function SignalCard({
  signal,
  position,
  total,
  isAdmin,
  pending,
  processId,
  onProcessChange,
  onEmail,
  onPhone,
  onLater,
  onDismiss,
  onArchiveAll,
  dismissRef,
}: SignalCardProps) {
  const processes = signal.open_opportunities;
  const contact = contactLabel({
    name: signal.primary_contact_name,
    title: signal.primary_contact_title,
    email: signal.primary_contact_email ?? signal.customer_email,
    phone: signal.primary_contact_phone ?? signal.customer_phone,
  });
  return (
    <SwipeCard label={`Signal: ${signal.headline}`} onDismiss={onDismiss} disabled={pending} dismissRef={dismissRef}>
      {(swipe) => {
        const busy = pending || swipe.leaving;
        return (
          <>

      <div className="flex flex-wrap items-center gap-1.5">
        {signal.effective_status && <CustomerStatusPill status={signal.effective_status} />}
        {signal.is_owner && <span className="rounded-pill bg-secondary px-3 py-0.5 text-[13px] font-medium">Din kunde</span>}
        {signal.recently_contacted && <StatusBadge tone="near" label="Nylig kontaktet" />}
        <span className="ml-auto text-sm tabular-nums text-muted-foreground">
          {position} / {total}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-3xl font-semibold leading-tight tracking-tight">
            <Link to={`/kunde/${signal.customer_id}`} className="underline-offset-4 hover:underline">
              {signal.customer_name}
            </Link>
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {signalTypeLabel(signal.signal_type)} · {signalSourceLabel(signal.source)} · {formatRelativeTime(signal.created_at)}
          </p>
        </div>
        <CustomerLogo name={signal.customer_name} logoUrl={signal.customer_logo_url} className="h-16 w-36 border-0 bg-transparent p-0 [&_img]:ml-auto" />
      </div>

      <section className="space-y-2 bg-muted px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Salgssignal</p>
        <p className="font-display text-xl font-medium leading-snug">{signal.headline}</p>
        {signal.evidence_quote && (
          <blockquote className="border-l-2 border-border pl-3 text-sm text-muted-foreground">{signal.evidence_quote}</blockquote>
        )}
        {signal.evidence_url && (
          <a href={signal.evidence_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            Åpne kilden <SrIcon name="external-link" className="h-3.5 w-3.5" />
          </a>
        )}
      </section>

      <section>
        <h4 className="text-sm font-semibold">Hvorfor Sølvrev bør ta kontakt</h4>
        <p className="mt-1 text-[15px] leading-relaxed">{signal.why_now}</p>
      </section>

      <dl className="grid gap-4 border-t border-border pt-3.5 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Kontakt</dt>
          <dd className="font-medium">
            {contact ? contact.primary : "Ingen registrert"}
            {contact?.secondary && <span className="block text-xs font-normal text-muted-foreground">{contact.secondary}</span>}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Tidligere verdi</dt>
          <dd className="font-medium">{formatNOK(signal.total_value)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Sist kontakt</dt>
          <dd className="font-medium">
            {signal.last_contact_at ? formatRelativeTime(signal.last_contact_at) : "Ingen registrert"}
            {signal.last_contact_at && (
              <span className="block text-xs font-normal text-muted-foreground">
                {activityTypeLabel(signal.last_contact_type as CustomerActivityType)} {formatDate(signal.last_contact_at)}
                {signal.last_contact_user_name ? `, ${signal.last_contact_user_name}` : ""}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {processes.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-muted px-3.5 py-2.5 text-sm">
          <span>
            {processes[0].owner_name ?? "Ingen"} har <b>{processes[0].title}</b> i {STAGE_LABELS[processes[0].stage as OpportunityStage] ?? processes[0].stage}
            {processes.length > 1 ? ` (+${processes.length - 1})` : ""}.
          </span>
          <span className="text-muted-foreground">Legg i</span>
          <Select value={processId ?? NEW_PROCESS} onValueChange={(v) => onProcessChange(v === NEW_PROCESS ? null : v)}>
            <SelectTrigger aria-label="Legg signalet i prosess" className="h-8 w-56 bg-card text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {processes.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
              <SelectItem value={NEW_PROCESS}>Ny prosess</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5">
        <Button className="h-12 text-[15px]" onClick={onEmail} disabled={busy} aria-keyshortcuts="1">
          <SrIcon name="mail" className="mr-2 h-4 w-4" />E-post<Kbd>1</Kbd>
        </Button>
        <Button variant="outline" className="h-12 text-[15px]" onClick={onPhone} disabled={busy} aria-keyshortcuts="2">
          Ring<Kbd>2</Kbd>
        </Button>
        <Button variant="secondary" className="h-12 text-[15px]" onClick={onLater} disabled={busy} aria-keyshortcuts="3">
          <SrIcon name="calendar" className="mr-2 h-4 w-4" />Følg opp senere<Kbd>3</Kbd>
        </Button>
        <Button
          variant="outline"
          className="h-12 border-border text-[15px] text-muted-foreground hover:border-destructive hover:text-destructive"
          onClick={swipe.dismiss}
          disabled={busy}
          aria-keyshortcuts="ArrowLeft"
        >
          Avvis<Kbd>←</Kbd>
        </Button>
      </div>

      {isAdmin && (
        <div className="flex justify-end text-sm">
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onArchiveAll} disabled={busy}>
            Arkiver for alle
          </Button>
        </div>
      )}
          </>
        );
      }}
    </SwipeCard>
  );
}
